import { z } from "zod";
import { MCP_FLAG } from "./mcp/constants";
import { ACCOUNT_CREATION_FLAG } from "./types/accountCreation";
import { GROUP_MAPPING_FLAG } from "./types/groupAccess";
import { tableNameCaseSchema } from "./types/instances";
import { MONITOR_FLAG } from "./types/monitor";
import type { TableNameCase } from "./warehouse/targetTableName";

/**
 * #2501 (owner 2026-11-02): a deployment policy, the one rule. A dedicated customer deployment
 * fixes its warehouse contract and its feature set in its environment file
 * (`environments/<env>.yaml` `deploymentPolicy`), which `env:generate` and main.bicep carry to the
 * web and the runner as one setting, `DEPLOYMENT_POLICY` (JSON). Nobody, a Skypoint Platform admin
 * included, changes a fixed value from a screen: the effective value is the policy's whatever the
 * stored binding or `features.flags` say, a save of another value is a 400 on its field, and the
 * screens render no control for it. A change is a reviewed pull request to the environment file
 * and a release. Absent (Skypoint's own deployments): nothing changes.
 *
 * Read by `scripts/environmentFile.ts` (validation), `server/config.ts` (the start refuses a
 * malformed value, `deployment_policy_invalid`), `server/services/featureFlags.ts`, the binding
 * reads and writes, the runner's load and its own flag reads, and skyMonitor's flag read.
 */

export const DEPLOYMENT_POLICY_SETTING = "DEPLOYMENT_POLICY";

/** The 400 a save of another value of a fixed field reads, on that field. */
export const FIXED_BY_DEPLOYMENT_MESSAGE = "Set for this deployment by Skypoint.";

/** The flag behind the Platform settings' preview controls (`settings.preview`). */
export const PREVIEW_SETTINGS_FLAG = "settings.preview";

/**
 * The features a policy may fix, by the name the environment file uses, to the `features.flags`
 * key the code reads. Closed: an unknown key is refused.
 */
export const POLICY_FEATURE_FLAGS = {
  mcp: MCP_FLAG,
  group_mapping: GROUP_MAPPING_FLAG,
  monitor: MONITOR_FLAG,
  invite_account_creation: ACCOUNT_CREATION_FLAG,
  "settings.preview": PREVIEW_SETTINGS_FLAG,
} as const;

export type PolicyFeatureName = keyof typeof POLICY_FEATURE_FLAGS;

/** The binding fields a policy may fix, by the binding view's name. */
export const POLICY_DESTINATION_FIELDS = ["tableNameCase", "metadataColumns"] as const;
export type PolicyDestinationField = (typeof POLICY_DESTINATION_FIELDS)[number];

const featureShape = Object.fromEntries(
  Object.keys(POLICY_FEATURE_FLAGS).map((name) => [name, z.boolean().optional()]),
) as Record<PolicyFeatureName, z.ZodOptional<z.ZodBoolean>>;

export const deploymentPolicySchema = z.strictObject({
  destination: z
    .strictObject({
      tableNameCase: tableNameCaseSchema.optional(),
      metadataColumns: z.boolean().optional(),
    })
    .optional(),
  features: z.strictObject(featureShape).optional(),
});

export type DeploymentPolicy = z.infer<typeof deploymentPolicySchema>;

/**
 * Parses the setting's raw value. Unset or blank: no policy. Anything else must be a JSON object
 * of the closed shape; the problem names keys and expectations only, never a value.
 */
export function parseDeploymentPolicy(raw: string | undefined): {
  policy: DeploymentPolicy | null;
  problem: string | null;
} {
  if (raw === undefined || raw.trim() === "") return { policy: null, problem: null };
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { policy: null, problem: "it is not JSON" };
  }
  const parsed = deploymentPolicySchema.safeParse(json);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const where = first?.path.join(".") || "(root)";
    return { policy: null, problem: `${where}: ${first?.message ?? "not valid"}` };
  }
  return { policy: parsed.data, problem: null };
}

/** The sentence the web's and the runner's start refuse with (`deployment_policy_invalid`). */
export function deploymentPolicyRefusal(raw: string | undefined): string | null {
  const { problem } = parseDeploymentPolicy(raw);
  return problem ? `${DEPLOYMENT_POLICY_SETTING} is refused: ${problem}` : null;
}

let cached: { raw: string | undefined; policy: DeploymentPolicy | null } | undefined;

/**
 * This process's policy, from `DEPLOYMENT_POLICY`. The start already refused a malformed value,
 * so a problem here is a test or a hand-set variable: it throws rather than read as no policy.
 */
export function deploymentPolicy(env: NodeJS.ProcessEnv = process.env): DeploymentPolicy | null {
  const raw = env[DEPLOYMENT_POLICY_SETTING];
  if (cached && cached.raw === raw) return cached.policy;
  const { policy, problem } = parseDeploymentPolicy(raw);
  if (problem) throw new Error(`${DEPLOYMENT_POLICY_SETTING} is refused: ${problem}`);
  cached = { raw, policy };
  return policy;
}

/** The `features.flags` keys the policy fixes, with their values. */
export function fixedFeatureFlags(
  policy: DeploymentPolicy | null = deploymentPolicy(),
): Readonly<Record<string, boolean>> {
  const out: Record<string, boolean> = {};
  for (const [name, value] of Object.entries(policy?.features ?? {})) {
    if (typeof value === "boolean") out[POLICY_FEATURE_FLAGS[name as PolicyFeatureName]] = value;
  }
  return out;
}

/** The policy's value of one `features.flags` key, or undefined when the policy leaves it free. */
export function fixedFeatureFlag(
  flag: string,
  policy: DeploymentPolicy | null = deploymentPolicy(),
): boolean | undefined {
  return fixedFeatureFlags(policy)[flag];
}

/** A flags document with the policy's values over the stored ones. */
export function withFeaturePolicy(
  flags: Readonly<Record<string, boolean>>,
  policy: DeploymentPolicy | null = deploymentPolicy(),
): Record<string, boolean> {
  return { ...flags, ...fixedFeatureFlags(policy) };
}

/** The binding fields the policy fixes, in `POLICY_DESTINATION_FIELDS` order. */
export function fixedDestinationFields(
  policy: DeploymentPolicy | null = deploymentPolicy(),
): PolicyDestinationField[] {
  const destination = policy?.destination ?? {};
  return POLICY_DESTINATION_FIELDS.filter((field) => destination[field] !== undefined);
}

/**
 * A binding row (or view) with the policy's destination values over the stored ones. Every reader
 * that names, creates or fills a table goes through it, so a stored value set before the policy
 * never reaches a load or a screen.
 */
export function withDestinationPolicy<
  T extends { tableNameCase?: TableNameCase | null; metadataColumns?: boolean | null },
>(row: T, policy: DeploymentPolicy | null = deploymentPolicy()): T {
  const destination = policy?.destination;
  if (!destination) return row;
  return {
    ...row,
    ...(destination.tableNameCase === undefined
      ? {}
      : { tableNameCase: destination.tableNameCase }),
    ...(destination.metadataColumns === undefined
      ? {}
      : { metadataColumns: destination.metadataColumns }),
  };
}

/**
 * The fields of a binding write that try another value than the policy's: each is a 400 on that
 * field with `FIXED_BY_DEPLOYMENT_MESSAGE`. Absent or the same value passes.
 */
export function destinationPolicyRefusals(
  body: { tableNameCase?: TableNameCase | null; metadataColumns?: boolean | null },
  policy: DeploymentPolicy | null = deploymentPolicy(),
): PolicyDestinationField[] {
  const destination = policy?.destination ?? {};
  return POLICY_DESTINATION_FIELDS.filter((field) => {
    const fixed = destination[field];
    const sent = body[field];
    return fixed !== undefined && sent !== undefined && sent !== fixed;
  });
}

/**
 * The flag keys of a `features.flags` write that try another value than the policy's. A key the
 * write leaves out passes: the effective value is the policy's either way.
 */
export function featurePolicyRefusals(
  value: unknown,
  policy: DeploymentPolicy | null = deploymentPolicy(),
): string[] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  const sent = value as Record<string, unknown>;
  return Object.entries(fixedFeatureFlags(policy))
    .filter(([key, fixed]) => key in sent && sent[key] !== fixed)
    .map(([key]) => key);
}
